import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { KeyRound, ArrowLeft, CheckCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const isValid = useMemo(() => password.length >= 8 && password === confirmPassword, [password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setError('');
    setLoading(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
        <div className="mb-8 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
        </div>
        <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)] text-center">
          <CardHeader className="space-y-1 pb-4">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle className="h-6 w-6" />
            </div>
            <CardTitle className="text-center text-2xl font-bold tracking-tight">Password updated</CardTitle>
            <CardDescription className="text-center">Your password has been reset successfully.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full">Go to login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
      <div className="mb-8 flex items-center justify-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
      </div>
      <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)]">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-center text-2xl font-bold tracking-tight">Jani</CardTitle>
          <CardDescription className="text-center">Set a new password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Badge variant="destructive" className="w-full justify-center py-1.5 text-xs font-medium rounded-md">{error}</Badge>
            )}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">New Password</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10" placeholder="Min. 8 characters" required minLength={8} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Confirm Password</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="pl-10" placeholder="Repeat password" required minLength={8} />
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-[var(--color-destructive)]">Passwords do not match</p>
              )}
            </div>
            <Button data-testid="reset-submit-button" type="submit" disabled={!isValid || loading} loading={loading} className="w-full">Reset Password</Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <Link to="/login" className="text-[var(--color-primary)] hover:underline inline-flex items-center"><ArrowLeft className="mr-1 h-4 w-4" /> Back to login</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
